import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TemplateDirective } from '@fleet-beat/core';
import { InfiniteListScrollDirective } from '@fleet-beat/infinite-list';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { EditorFieldType, EditorFieldTypeEnumService } from '../enums/editor-field-type-enum.service';
import {
  EditorSpecialItemType,
  EditorSpecialItemTypeEnumService,
} from '../enums/editor-special-item-type-enum.service';
import { EditorConfig } from '../models/editor-config.model';
import { EditorHelperService } from '../services/editor-helper.service';

@Component({
  selector: 'fb-editor',
  templateUrl: './editor.component.html',
  styles: [':host { display: block; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorComponent extends OnDestroyMixin implements OnInit {
  @Input()
  set config(config: EditorConfig) {
    this.configSubject.next(config);
  }
  get config(): EditorConfig {
    return this.configSubject.getValue();
  }

  @Input()
  set form(value: FormGroup) {
    this.formSubject.next(value);
  }
  get form(): FormGroup {
    return this.formSubject.getValue();
  }

  @Input() infiniteListScroll: InfiniteListScrollDirective;
  @Input() templates: TemplateDirective<object>[];

  @Output() formCreated: EventEmitter<FormGroup> = new EventEmitter();

  // NOTE: For debug purposes (only on DEV and QA). Type in console window.tmsDebug = true
  get showDebug(): boolean {
    return Boolean((window as { tmsDebug?: boolean }).tmsDebug);
  }

  @ContentChildren(TemplateDirective) childrenTemplates: QueryList<TemplateDirective<object>>;

  readonly fieldTypeEnum: Record<string, EditorFieldType> = this.editorFieldTypeEnumService.getAll();
  readonly specialItemTypeEnum: Record<string, EditorSpecialItemType> = this.editorSpecialItemTypeEnumService.getAll();

  private configSubject: BehaviorSubject<EditorConfig> = new BehaviorSubject(undefined);
  private formSubject: BehaviorSubject<FormGroup> = new BehaviorSubject(undefined);

  constructor(
    private editorFieldTypeEnumService: EditorFieldTypeEnumService,
    private editorSpecialItemTypeEnumService: EditorSpecialItemTypeEnumService,
    private editorHelperService: EditorHelperService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.configSubject
      .pipe(
        untilComponentDestroyed(this),
        filter(Boolean),
        filter(() => !this.form),
        map((config: EditorConfig) => this.editorHelperService.createForm(config)),
      )
      .subscribe((form: FormGroup) => {
        this.form = form;
      });

    this.formSubject.pipe(untilComponentDestroyed(this), filter(Boolean)).subscribe((form: FormGroup) => {
      this.formCreated.emit(form);

      form.statusChanges.pipe(untilComponentDestroyed(this), distinctUntilChanged()).subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
    });
  }
}
